'use client';

import dynamic from 'next/dynamic';

import { useDisclosure } from '@/app/hooks/useDisclosure';
import { SPARTAN_PRODUCT_ACCESSORIES_REMARK_LABELS } from '@/app/lcv/components/sections/spartan/products/accessories/remark/constants';
import type { SpartanProductAccessoriesRemarkNoteProps } from '@/app/lcv/components/sections/spartan/products/accessories/remark/types';
import {
  SPARTAN_PRODUCT_ACCESSORIES_DATA_GA,
  SPARTAN_PRODUCT_ACCESSORIES_TRACKING,
} from '@/app/lcv/components/sections/spartan/products/accessories/tracking';
import { SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

const SpartanProductAccessoriesRemarkModal = dynamic(
  () =>
    import('@/app/lcv/components/sections/spartan/products/accessories/remark/modal').then(
      (mod) => mod.SpartanProductAccessoriesRemarkModal
    ),
  { ssr: false }
);

export const SpartanProductAccessoriesRemarkNote = ({
  remark,
  className,
  dataGAGroup = SPARTAN_PRODUCT_ACCESSORIES_DATA_GA.section,
}: SpartanProductAccessoriesRemarkNoteProps) => {
  const [isOpen, { open, close }] = useDisclosure(false);

  const handleOpenRemark = () => {
    open();
  };

  const handleCloseRemark = () => {
    close();
  };

  return (
    <div
      className={cn('text-caption max-w-[768px] text-gray-600', className)}
      {...createDataAttributes({
        test: SPARTAN_PRODUCT_ACCESSORIES_TRACKING.remark,
      })}
    >
      <span>
        <strong>{SPARTAN_PRODUCT_ACCESSORIES_REMARK_LABELS.noteLabel}</strong>
        {' : '}
        {remark.note ? (
          <span
            className="where-span:text-inline-wrap"
            dangerouslySetInnerHTML={{ __html: remark.note }}
          />
        ) : (
          SPARTAN_PRODUCT_ACCESSORIES_REMARK_LABELS.noteText
        )}{' '}
      </span>
      <button
        type="button"
        className={cn(
          'link cursor-pointer',
          SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME
        )}
        onClick={handleOpenRemark}
        {...createDataAttributes({
          test: SPARTAN_PRODUCT_ACCESSORIES_TRACKING.remarkButton,
        })}
        {...createDataGA({
          click: true,
          eventName: SPARTAN_PRODUCTS_EVENT_NAME,
          group: dataGAGroup,
          text: SPARTAN_PRODUCT_ACCESSORIES_REMARK_LABELS.terms,
          value: 'open_term_condition',
        })}
      >
        {SPARTAN_PRODUCT_ACCESSORIES_REMARK_LABELS.seeDetails}
      </button>
      {!!isOpen && (
        <SpartanProductAccessoriesRemarkModal
          isOpen={isOpen}
          title={remark.title}
          detail={remark.detail}
          dataGAGroup={dataGAGroup}
          onClose={handleCloseRemark}
        />
      )}
    </div>
  );
};
